import { useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { alertState } from "./AlertDisplay/AlertDisplay.state";
import { useMutation } from "react-query";
import externalAxios from "axios";
import { axios } from "../lib/request";
import useImageThumbnail from "./ImageThumbnail/useImageThumbnail";

const findThumbURL = (collection, key, type) => {
    const assets = collection.assets

    let asset = (assets?.[`${type}s`] || []).find(
        (asset) => asset.name === key && (asset.urls?.png || asset.url)
    );

    if (!asset) {
        // Branding assets are in the root level of the collection object
        asset = collection?.[type]
    }

    // Branding's tour asset url:
    return asset?.[key]?.url ||
        // Normal asset url:
        asset?.url ||
        // Fallback url:
        null
};

const readFileURL = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = ({ target }) => resolve(target.result);
        reader.readAsDataURL(file);
    });
};

const translateKey = (key) => {
    switch (key) {
        case "ya":
            return "correct";
        case "naw":
            return "incorrect";
        case "challengesenabled":
            return "challenges";
        case "streamenabled":
            return "stream";
        case "leaderboardenabled":
            return "leaderboard";
        case "npcsenabled":
            return "npcs";
        case "reelsenabled":
            return "reels";
        case "rewardsenabled":
            return "rewards";
        case "joinenabled":
            return "join";
        case "notificationsenabled":
            return "notifications";
        case "helpenabled":
            return "help";
        case "customenabled":
            return "custom";
        case "overlay":
            return "sticker";
        case "tour_1":
            return 0;
        case "tour_2":
            return 1;
        case "tour_3":
            return 2;
        case "tour_4":
            return 3;
        default:
            return key;
    }
};

const useImageUpload = ({ name, type = "photo", useQuery, standalone = false }) => {
    const [{ id = '' }] = useQuery();
    const [collection, setCollection] = useQuery({ invalidate: false });
    const keyMap = {
        photo: 'standard',
        arrival_image: 'arrival'
    }
    const key = keyMap[name] || name;
    const { t } = useTranslation();
    const setAlert = useSetRecoilState(alertState);

    const showError = (e) => {
        const status = e?.response?.status || e?.status;
        let message;

        // 500 error is a server error, so we need to show a different message
        if (status === 500) {
            message = t('An error has occurred with generating your image. Please try again later.');
        } else {
            const serverMessage = e?.error || e?.response?.data?.error || e?.message;
            // If the server message is not available, show a generic error message
            message = serverMessage || t('An error has occurred.');
        }

        setAlert({
            type: "danger",
            message,
            closable: true,
            autoClose: true,
        });
    };

    const thumb = findThumbURL(
        // Assets are sometimes at the root level of the collection object,
        // e.g. assets for branding
        collection,
        translateKey(key),
        type
    );
    const [placeholder, setPlaceholder] = useState(!thumb);
    const [preview, setPreview] = useState(!placeholder);
    const updateTimeout = 1000;
    let timeoutRef = null

    const { isLoading, mutateAsync } = useMutation(async (file) => {
        const fileURL = await readFileURL(file);
        const [ext] = file.name.split(".").reverse();
        const url = `${process.env.REACT_APP_API_SIGN_URL
            }/${ext.toLocaleLowerCase()}`;
        const response = await axios.get(url);
        const { data } = response || {}
        const options = { headers: { "Content-Type": file.type } };
        await externalAxios.put(data.signature, file, options);

        const [uuid, extension] = data.name.split(".");
        timeoutRef = setTimeout(
            commitMutation({ uuid, extension }, fileURL),
            updateTimeout
        );
    });

    const { isLoading: isLoadingAi, mutateAsync: mutateAsyncAi } = useMutation(async (prompt) => {
        const url = `${process.env.REACT_APP_API_GENERATE_URL}`;
        const response = await axios.post(url, { prompt, generate_type: 1 });
        const { data } = response || {}
        const { uuid, extension, url: fileURL } = data || {};
        timeoutRef = setTimeout(
            commitMutation({ uuid, extension }, fileURL),
            updateTimeout
        );
    });

    const {
        errorState: [error],
        loadedState: [loaded, setLoaded],
        mediaIsProcessing,
    } = useImageThumbnail({ thumb, isLoading })

    const commitMutation = (file, fileURL) => {
        // When file payloads are all nulls
        // it clears the existing media
        const { uuid = null, extension = null } = file || {}
        // watermark and standalone media do not need `media` prefix in the payload
        const payloadPrefix = (['watermark'].includes(type) || standalone)
            ? `${name}`
            : `${name}_media`
        let media = (collection.assets?.[`${type}s`] || []).filter(
            (asset) => asset.name !== translateKey(key)
        );
        let assets = undefined;
        let mutationPayload = {}

        if (type === "tour") {
            // tour medias are arrays containing asset urls
            const index = translateKey(key);
            media = collection.tour[index];

            const tour = [
                ...collection.tour.slice(0, index),
                { ...media, url: fileURL },
                ...collection.tour.slice(index + 1),
            ];

            mutationPayload = { tour }
        } else if (media) {
            // common medias are stored in the assets object
            assets = {
                ...collection.assets,
                [`${type}s`]: [...media, { name: translateKey(key), url: fileURL }],
            };

            mutationPayload = { assets }
        } else {
            // branding medias are stored in the root collection object
            mutationPayload = {
                [type]: {
                    url: fileURL,
                }
            }
        }
        setCollection({
            ...mutationPayload,
            [`${payloadPrefix}_uuid`]: uuid,
            [`${payloadPrefix}_extension`]: extension
                ? extension.toLocaleLowerCase()
                : extension,
        })
    }

    const upload = async (file) => {
        if (file) {
            try {
                setPlaceholder(false);
                setPreview(true);
                setLoaded(true);
                await mutateAsync(file);
                clearTimeout(timeoutRef)
            } catch (e) {
                console.error(e);
                showError(e);
            }
        }
    };
    
    const generateImage = async (prompt) => {
        if (prompt) {
            try {
                setPlaceholder(false);
                setPreview(true);
                setLoaded(true);
                await mutateAsyncAi(prompt);
                clearTimeout(timeoutRef)
            } catch (e) {
                console.error(e);
                showError(e);
            }
        }
    };

    const clear = () => {
        commitMutation(null, '');
        setPlaceholder(true);
        setPreview(false);
        setLoaded(true);
    };

    const fileId = `${name}-${type}-${id}-upload`

    return {
        fileId,
        thumb,
        previewState: [preview, setPreview],
        placeholderState: [placeholder, setPlaceholder],
        errorState: [error],
        loadedState: [loaded, setLoaded],
        upload,
        generateImage,
        clear,
        isLoading,
        isLoadingAi,
        mediaIsProcessing,
    }
};

export default useImageUpload;
